/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Save,
} from 'lucide-react';
import ROUTE from 'src/utils/routeConstants';
import { isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { isTyreManagementV2Enabled } from 'src/utils/tyreManagementFeature';
import { useTheme as useAppTheme } from 'src/context/ThemeContext';
import PageBreadcrumb, { hasNestedLinks } from 'src/features/tyre-management/components/PageBreadcrumb';
import { Button } from 'src/features/tyre-management/components/ui/button';
import { Input } from 'src/features/tyre-management/components/ui/input';
import { Label } from 'src/features/tyre-management/components/ui/label';
import SelectControl from 'src/features/tyre-management/components/SelectControl';
import VehicleFormSections from 'src/features/vehicle-list/forms/VehicleFormSections';
import {
  formFromVehicle,
  normalizeVehiclePayload,
  parseFieldErrors,
  validateVehicleForm,
} from 'src/features/vehicle-list/forms/vehicleFormState';
import { vehicleDetailsApi } from './api/vehicleDetailsApi';
import { vehicleDetailsQueryKeys } from './api/queryKeys';
import useVehicleMutation from './hooks/useVehicleMutation';
import 'src/features/tyre-management/tyre-management.css';
import './vehicle-details.css';

const sectionLinks = [
  ['edit-identity', 'Identity'], ['edit-assignment', 'Assignment'],
  ['edit-operation', 'Fuel & operation'], ['edit-telematics', 'Tracking (optional)'],
  ['edit-modules', 'Tyre tracking'], ['service-policy', 'Service policy'],
];

const editCrumbs = (vehicleId, registration) => [
  { label: 'Vehicle Management' },
  { label: 'Vehicles', href: ROUTE.vehicles },
  {
    label: registration || 'Vehicle',
    href: `${ROUTE.vehicles}/${vehicleId}`,
    mono: Boolean(registration),
  },
  { label: 'Edit' },
];

const asList = (payload) => payload?.results || payload || [];

const VehicleEditPage = () => {
  const { vehicleId } = useParams();
  const history = useHistory();
  const { mode } = useAppTheme();
  const appSettings = useSelector((state) => state.settings.appSettings);
  const assetCodeMappingEnabled = Boolean(appSettings?.asset_code_mapping_enabled);
  const globalTyreEnabled = isTyreManagementV2Enabled(appSettings)
    && !isFleetOnlyModeEnabled(appSettings);
  const [vehicle, setVehicle] = useState(null);
  const [form, setForm] = useState(null);
  const [policy, setPolicy] = useState({ meter_kind: 'distance_km', interval_value: '', setup_state: 'incomplete' });
  const [options, setOptions] = useState({
    sites: [], groups: [], departments: [], contractors: [], tanks: [],
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const detailsQuery = useQuery({
    queryKey: [...vehicleDetailsQueryKeys.options(), String(vehicleId)],
    queryFn: () => Promise.all([
      vehicleDetailsApi.getVehicle(vehicleId),
      vehicleDetailsApi.getServicePolicy(vehicleId),
      vehicleDetailsApi.getSites(),
      vehicleDetailsApi.getGroups(),
      vehicleDetailsApi.getDepartments(),
      vehicleDetailsApi.getContractors(),
      vehicleDetailsApi.getTanks(),
    ]).then(([details, servicePolicy, sites, groups, departments, contractors, tanks]) => ({
      details,
      servicePolicy,
      options: {
        sites: asList(sites),
        groups: asList(groups),
        departments: asList(departments),
        contractors: asList(contractors),
        tanks: asList(tanks),
      },
    })),
    enabled: Boolean(vehicleId),
  });

  useEffect(() => {
    if (!detailsQuery.data || dirty) return;
    const { details, servicePolicy, options: loadedOptions } = detailsQuery.data;
    setVehicle(details);
    setForm(formFromVehicle(details));
    if (servicePolicy?.meter_kind) setPolicy(servicePolicy);
    else if (details.type === 'Non Movable' || details.type === 'Semi Movable') {
      setPolicy({ meter_kind: 'engine_hours', interval_value: '', setup_state: 'review_required' });
    } else if (Number(details.service_interval) > 0) {
      setPolicy({ meter_kind: 'distance_km', interval_value: details.service_interval, setup_state: 'active' });
    } else {
      setPolicy({ meter_kind: 'distance_km', interval_value: '', setup_state: 'incomplete' });
    }
    setOptions(loadedOptions);
  }, [detailsQuery.data, dirty]);

  const saveVehicleMutation = useVehicleMutation(vehicleId, async ({ vehiclePayload, servicePolicy }) => {
    await vehicleDetailsApi.updateVehicle(vehicleId, vehiclePayload);
    if (Number(servicePolicy.interval_value) > 0) {
      await vehicleDetailsApi.updateServicePolicy(vehicleId, {
        meter_kind: servicePolicy.meter_kind,
        interval_value: servicePolicy.interval_value,
        setup_state: servicePolicy.setup_state,
      });
    }
  });

  useEffect(() => {
    if (!dirty) return undefined;
    const unblock = history.block('You have unsaved vehicle changes. Leave without saving?');
    const beforeUnload = (event) => {
      event.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => { unblock(); window.removeEventListener('beforeunload', beforeUnload); };
  }, [dirty, history]);

  const setField = (key, transform = (next) => next) => (event) => {
    const nextValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: transform(nextValue) }));
    setDirty(true);
    setSuccess('');
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
  };
  const setCheckbox = (key) => (checked) => {
    setForm((current) => ({ ...current, [key]: checked }));
    setDirty(true);
    setSuccess('');
  };
  const setPolicyField = (key) => (event) => {
    setPolicy((current) => ({ ...current, [key]: event.target.value }));
    setDirty(true);
    setSuccess('');
  };

  const payload = useMemo(
    () => (form ? normalizeVehiclePayload(form, {
      includeTyreManagement: globalTyreEnabled,
    }) : null),
    [form, globalTyreEnabled],
  );

  const save = async (event) => {
    event.preventDefault();
    const clientErrors = await validateVehicleForm(form, { assetCodeMappingEnabled, isEdit: true });
    if (Object.keys(clientErrors).length) {
      setFieldErrors(clientErrors);
      setError('Complete the required vehicle fields before saving.');
      return;
    }
    if (policy.interval_value !== '' && Number(policy.interval_value) <= 0) {
      setError('Service interval must be greater than zero.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveVehicleMutation.mutateAsync({ vehiclePayload: payload, servicePolicy: policy });
      setDirty(false);
      setSuccess('Vehicle changes saved.');
      setTimeout(() => history.push(`/app/vehicles/vehicle-list/${vehicleId}#vehicle-record`), 250);
    } catch (requestError) {
      setFieldErrors(parseFieldErrors(requestError.fields));
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (detailsQuery.isLoading) {
    return (
      <main className="tyre-management-v2 vehicle-details-v2 vd-loading-page" data-theme={mode}>
        <div className="vd-edit-crumb">
          <PageBreadcrumb items={editCrumbs(vehicleId)} />
        </div>
        <div className="vd-skeleton vd-skeleton--header" />
        <div className="vd-skeleton vd-skeleton--wide mt-6" />
      </main>
    );
  }
  if ((error || detailsQuery.error) && !form) {
    return (
      <main className="tyre-management-v2 vehicle-details-v2 vd-fatal-state" data-theme={mode}>
        <AlertTriangle />
        <h1>Vehicle editor unavailable</h1>
        <p>{error || detailsQuery.error?.message}</p>
        <Button onClick={() => history.goBack()}>Go back</Button>
      </main>
    );
  }

  const crumbs = editCrumbs(vehicleId, vehicle.registration_num);

  return (
    <main className="tyre-management-v2 vehicle-details-v2 vd-edit-page min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8" data-theme={mode}>
      <Helmet>
        <title>
          Edit
          {vehicle.registration_num}
          {' '}
          | Vehicle Details
        </title>
      </Helmet>
      <div className="vd-edit-crumb">
        <PageBreadcrumb items={crumbs} />
      </div>
      <header className="vd-edit-header">
        {!hasNestedLinks(crumbs) && (
          <Button variant="ghost" size="icon" aria-label="Back to vehicle details" onClick={() => history.push(`${ROUTE.vehicles}/${vehicleId}`)}>
            <ArrowLeft />
          </Button>
        )}
        <div className="vd-edit-header__identity">
          <p className="vd-eyebrow">Edit vehicle</p>
          <h1 className="vd-registration">{vehicle.registration_num}</h1>
        </div>
        <div className="vd-edit-header__actions">
          <Button variant="outline" onClick={() => history.push(`/app/vehicles/vehicle-list/${vehicleId}`)}>Cancel</Button>
          <Button type="submit" form="vehicle-edit-form" disabled={saving}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </header>
      <div className="vd-edit-layout">
        <nav className="vd-section-index" aria-label="Edit sections">{sectionLinks.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}</nav>
        <form id="vehicle-edit-form" className="vd-edit-form" onSubmit={save}>
          {error && <div className="vd-form-error" role="alert">{error}</div>}
          {success && (
          <div className="vd-form-success" role="status">
            <CheckCircle2 />
            {success}
          </div>
          )}
          <VehicleFormSections
            form={form}
            setField={setField}
            setCheckbox={setCheckbox}
            options={options}
            errors={fieldErrors}
            assetCodeMappingEnabled={assetCodeMappingEnabled}
            originalType={vehicle.type}
            globalTyreEnabled={globalTyreEnabled}
            tyreManagementEnabledAt={vehicle?.tyre_management_enabled_at ?? null}
          />
          <section id="service-policy" className="vd-edit-section vd-section-anchor">
            <div>
              <p className="vd-eyebrow">Service policy</p>
              <h2>Choose one authoritative service meter</h2>
              <p className="vd-edit-help">Changing movement type does not silently change this policy. Review ambiguous legacy vehicles explicitly.</p>
            </div>
            <div className="vd-form-grid">
              <div>
                <Label htmlFor="meter-kind">Primary service meter</Label>
                <SelectControl id="meter-kind" value={policy.meter_kind} onChange={setPolicyField('meter_kind')}>
                  <option value="distance_km">Distance (km)</option>
                  <option value="engine_hours">Engine hours</option>
                </SelectControl>
              </div>
              <div>
                <Label htmlFor="interval">Service interval (optional)</Label>
                <Input id="interval" type="number" min="0" step="0.001" value={policy.interval_value} onChange={setPolicyField('interval_value')} />
              </div>
              <div>
                <Label htmlFor="policy-state">Policy state</Label>
                <SelectControl id="policy-state" value={policy.setup_state} onChange={setPolicyField('setup_state')}>
                  <option value="active">Active</option>
                  <option value="review_required">Review required</option>
                  <option value="incomplete">Incomplete</option>
                </SelectControl>
              </div>
            </div>
          </section>
        </form>
      </div>
    </main>
  );
};

export default VehicleEditPage;
